/**
 * Zoho SalesIQ live chat (loaded in index.html). Signed-in users reach it through the
 * AI support chat: its own button stays hidden until the agent hands the customer to a
 * person. Signed-out pages keep the SalesIQ button, since the AI chat needs a sign-in.
 *
 * Docs: https://www.zoho.com/salesiq/help/developer-section/js-api.html
 */

// Questions longer than this are cut; the full summary is on the support ticket.
const MAX_QUESTION_LENGTH = 1000

const salesIQ = () => window.$zoho?.salesiq

// The widget script replaces $zoho.salesiq with the real API and calls ready() once
// loaded. Run fn now if that has happened, otherwise chain it onto ready().
const whenReady = (fn) => {
  const siq = salesIQ()
  if (!siq) return
  if (siq.floatbutton) {
    fn(siq)
    return
  }
  const previous = siq.ready
  siq.ready = function (...args) {
    if (typeof previous === 'function') previous.apply(this, args)
    fn(salesIQ())
  }
}

// Set by the SalesIQ ready handler in index.html.
const OPERATORS_EVENT = 'televu:salesiq-operators'
const operatorsOnline = () => (typeof window.televuSupportOperatorsOnline === 'boolean' ? window.televuSupportOperatorsOnline : null)

/** Hide the SalesIQ button while the AI support chat is the way in. */
export const hideSalesIQButton = () => whenReady(siq => siq.floatbutton.visible('hide'))

/** Put the SalesIQ button back (signed out, or the AI chat is not available). */
export const showSalesIQButton = () => whenReady(siq => siq.floatbutton.visible('show'))

/**
 * Follow whether a TeleVU team member is available. listener(true|false|null) is called
 * now and on every change; null means unknown (SalesIQ not loaded yet, or blocked).
 * Returns an unsubscribe function.
 */
export const watchOperatorsOnline = (listener) => {
  const onChange = (e) => listener(e.detail)
  window.addEventListener(OPERATORS_EVENT, onChange)
  listener(operatorsOnline())
  return () => window.removeEventListener(OPERATORS_EVENT, onChange)
}

/**
 * Open a live chat with the TeleVU team, primed with who the customer is and the AI
 * agent's summary. Zoho only allows chat.start() inside a click handler, so call this
 * directly from one. Returns false if SalesIQ did not load (ad blocker, network policy).
 */
export const startHumanChat = ({ name, email, portal, summary, conversationId }) => {
  const siq = salesIQ()
  if (!siq?.floatbutton) return false
  if (name) siq.visitor.name(name)
  if (email) siq.visitor.email(email)
  siq.visitor.info({ Portal: portal || '', 'AI support conversation': conversationId || '' })
  const question = summary.length > MAX_QUESTION_LENGTH ? `${summary.slice(0, MAX_QUESTION_LENGTH - 3)}...` : summary
  siq.visitor.question(question)
  siq.floatbutton.visible('show')
  siq.floatwindow.visible('show')
  siq.chat.start()
  return true
}
